import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { HYDRATE } from 'next-redux-wrapper';
import { states } from '../lib/booking/reservation-states';
import type { WelcomeDrinkCategory } from '../lib/booking/welcomeDrinks';
import dateUtil, { quartileToDate } from '../lib/dayjs';
import { BASE_URL } from './constants';
import type { VenueKey } from '../lib/booking/venueKey';
// Define a service using a base URL and expected endpoints
/**
 * Utilizes @reduxjs/toolkit createApi to create an api service.
 * @see https://redux-toolkit.js.org/rtk-query/api/createApi
 */
export const bookingApi = createApi({
  reducerPath: 'bookingApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/booking',
    prepareHeaders: (headers, { type }) => {
      // Only set Content-Type for mutations (POST, PUT, DELETE), not queries (GET)
      if (type === 'mutation') {
        headers.set('Content-Type', 'application/json');
      }
      return headers;
    },
  }),
  extractRehydrationInfo(action, { reducerPath }) {
    if (action.type === HYDRATE) {
      return action.payload[reducerPath];
    }
  },
  endpoints: (build) => ({
    /**
     * Query availability
     * @param {QueryAvailabilityParams} params
     * @returns {AvailabilityResponse}
     * @example
     * const { data, error } = await bookingApi.endpoints.queryAvailability.initiate({
     *  venueId: 'se-gbg-heden',
     *  date: '2021-01-01',
     *  seatings: 2,
     * });
     */
    queryAvailability: build.query<AvailabilityResponse, QueryAvailabilityParams>({
      // booking-api's function takes the venue as `placeId` and accepts the slug there.
      query: ({ date, seatings, venueId: placeId, zone = 'indoors', originalReservation }) => {
        const d = dateUtil(date);

        const weekday = new Date(date).getUTCDay();
        return {
          url: `/query-availability`,
          method: 'GET',
          params: {
            dateString: d.format('YYYYMMDD'),
            day: weekday,
            justThisQuartile: false,
            placeId,
            seatings,
            zone,
            originalReservation: originalReservation || 'none',
          },
        };
      },
    }),
    queryOpenStatus: build.query<QueryOpenStatusResponse, QueryOpenStatusParams>({
      query: ({ startDate, endDate, seatings, venueId: placeId, zone = 'indoors' }) => {
        const start = dateUtil(startDate);
        const end = dateUtil(endDate);
        return {
          url: `/query-open-status`,
          method: 'GET',
          params: {
            startDate: start.format('YYYYMMDD'),
            endDate: end.format('YYYYMMDD'),
            placeId,
            seatings,
            zone,
          },
        };
      },
    }),
    createReservation: build.mutation<CreateReservationResponse, CreateReservationParams>({
      query: ({
        venueId: placeId,
        seatings,
        kids = 0,
        date,
        startQuartile,
        endQuartile,
        name,
        phone,
        zone = 'indoors',
        email = null,
        acceptMail = false,
        comment = '',
        tags = [],
        welcomeDrinks,
        ref = null,
        inTheCircus = null,
        client,
        lang,
        fromWaitlist = null,
        fromWaitlistId = null,
        sessionId = null,
        fingerprint = null,
        attribution = null,
        captchaToken = '',
        honeypot = '',
        formStartedAt = null,
      }) => {
        const d = dateUtil(date);
        const adults = seatings - kids; // Calculate adults
        return {
          url: '/reservation',
          method: 'POST',
          body: JSON.stringify({
            placeId,
            seatings,
            adults,
            kids,
            dateString: d.format('YYYYMMDD'),
            startQuartile,
            endQuartile,
            name,
            phone,
            email,
            acceptMail,
            comment,
            tags,
            welcomeDrinks,
            ref,
            inTheCircus,
            zone,
            client,
            lang,
            fromWaitlist,
            fromWaitlistId,
            sessionId,
            fingerprint,
            attribution,
            captchaToken,
            honeypot,
            formStartedAt,
          }),
        };
      },
    }),
    createRequest: build.mutation<CreateReservationResponse, CreateRequestOrWaitlistParams>({
      query: ({
        venueId: placeId,
        seatings,
        kids = 0,
        date,
        name,
        phone,
        email,
        acceptMail = false,
        zone = 'indoors',
        comment = '',
        tags = [],
        welcomeDrinks,
        ref = null,
        inTheCircus = null,
        client,
        lang,
        sessionId = null,
        fingerprint = null,
        attribution = null,
        captchaToken = '',
        honeypot = '',
        formStartedAt = null,
      }) => {
        const d = dateUtil(date);
        const adults = seatings - kids; // Calculate adults
        return {
          url: '/reservation/request',
          method: 'POST',
          body: JSON.stringify({
            placeId,
            dateString: d.format('YYYYMMDD'),
            timeString: d.format('HH:mm'),
            seatings,
            adults,
            kids,
            name,
            phone,
            comment,
            tags,
            welcomeDrinks,
            email,
            acceptMail,
            zone,
            client,
            ref,
            inTheCircus,
            lang,
            sessionId,
            fingerprint,
            attribution,
            captchaToken,
            honeypot,
            formStartedAt,
          }),
        };
      },
    }),
    createWaitlistEntry: build.mutation<CreateReservationResponse, CreateRequestOrWaitlistParams>({
      query: ({
        venueId: placeId,
        seatings,
        kids = 0,
        date,
        name,
        phone,
        email,
        zone = 'indoors',
        comment = '',
        tags = [],
        welcomeDrinks,
        client,
        lang,
        startQuartile,
        endQuartile,
        waitlistQuartiles,
        acceptMail = false,
        inTheCircus = false,
        ref = null,
        sessionId = null,
        fingerprint = null,
        attribution = null,
        captchaToken = '',
        honeypot = '',
        formStartedAt = null,
      }) => {
        const d = dateUtil(date);
        const adults = seatings - kids; // Calculate adults
        return {
          url: '/reservation/waitlist',
          method: 'POST',
          body: JSON.stringify({
            placeId,
            dateString: d.format('YYYYMMDD'),
            timeString: d.format('HH:mm'),
            seatings,
            adults,
            kids,
            name,
            phone,
            comment,
            tags,
            welcomeDrinks,
            email,
            zone,
            client,
            lang,
            startQuartile,
            endQuartile,
            waitlistQuartiles,
            acceptMail,
            inTheCircus,
            ref,
            sessionId,
            fingerprint,
            attribution,
            captchaToken,
            honeypot,
            formStartedAt,
          }),
        };
      },
    }),
    sendMessage: build.mutation<{}, SendMessageParams>({
      query: ({ reservationId, text }) => {
        return {
          url: '/message',
          method: 'POST',
          body: JSON.stringify({
            reservationId,
            text,
          }),
        };
      },
    }),
    cancelReservation: build.mutation<Reservation, CancelReservationParams>({
      query: ({ venueId, placeId, id, reason, tags, cancelText }) => {
        const params = new URLSearchParams();
        // The reservation's venueId; its placeId only if it has none. Never both.
        if (venueId) {
          params.append('venueId', venueId);
        } else if (placeId) {
          params.append('placeId', placeId);
        }
        params.append('reason', reason);
        params.append('cancelText', cancelText);

        tags.forEach((tag) => params.append('tags', encodeURIComponent(tag)));

        const stringParams = params.toString();

        return {
          url: `/reservation/${id}${stringParams ? `?${stringParams}` : ''}`,
          method: 'DELETE',
        };
      },
    }),
    getReservation: build.query<Reservation, string>({
      query: (id) => {
        return {
          url: `/reservation/${id}`,
        };
      },
    }),
    getReservations: build.query<{ results: Reservation[] }, string[]>({
      query: (ids) => {
        return {
          url: `/reservations`,
          params: {
            ids,
          },
        };
      },
    }),
    updateReservation: build.mutation<UpdateReservationResponse, UpdateReservationParams>({
      query: ({
        id,
        name,
        date,
        startQuartile,
        endQuartile,
        phone,
        email,
        acceptMail,
        comment,
        tags,
        welcomeDrinks,
        seatings,
        kids,
        table,
        zone,
        client,
        lang,
      }) => {
        const d = dateUtil(date);
        const adults = seatings - kids; // Calculate adults
        const payload = {
          id,
          name,
          dateString: d.format('YYYYMMDD'),
          startQuartile,
          endQuartile,
          phone,
          email,
          acceptMail,
          comment,
          tags,
          welcomeDrinks,
          seatings,
          adults,
          kids,
          tableName: table ? table.name : null,
          // updating the reservation should always reset the state to NEW, to indicate that the restaurant
          // has not seen this version of the reservation
          state: states.NEW,
          zone,
          client,
          lang,
        };

        // Remove all null and undefined values from the payload
        Object.keys(payload).forEach((key) => {
          if (payload[key] === null || payload[key] === undefined) {
            delete payload[key];
          }
        });

        return {
          url: `/reservation/${id}`,
          method: 'POST',
          body: JSON.stringify(payload),
        };
      },
    }),
    getEvents: build.query<any[], string>({
      query: (venueId) => {
        return {
          url: '/events',
          params: { venueId },
        };
      },
    }),
    /** The venue's city pool (booking-api partnerVenues): its sister venues, as slugs. */
    getPartnerVenues: build.query<{ venueIds: string[] }, string>({
      query: (venueId) => ({
        url: '/partners',
        params: { venueId },
      }),
      keepUnusedDataFor: 300,
    }),
    getWaitlistHighDemand: build.query<
      { waitlistHighDemand: boolean },
      { dateString: string } & VenueKey
    >({
      query: ({ dateString, ...venueKey }) => {
        return {
          url: '/waitlist-high-demand',
          params: { dateString, ...venueKey },
        };
      },
    }),
    getPresaleCatalog: build.query<
      WelcomeDrinkCategory,
      { venueId: string; lang?: string; minorUnitExponent?: number }
    >({
      query: ({ venueId, lang, minorUnitExponent }) => ({
        url: `/presale/${venueId}`,
        params: {
          ...(lang ? { lang } : {}),
          ...(minorUnitExponent != null ? { minorUnitExponent } : {}),
        },
      }),
    }),
    createPaymentSession: build.mutation<CreatePaymentResponse, CreatePaymentSessionParams>({
      query: (body) => ({
        url: '/payment/session',
        method: 'POST',
        body: JSON.stringify(body),
      }),
    }),
    submitAdyenPayment: build.mutation<AdyenAdvancedAttemptResponse, AdyenAdvancedAttemptParams>({
      query: ({ transactionId, data, locale }) => ({
        url: `/payment/${encodeURIComponent(transactionId)}/payments`,
        method: 'POST',
        params: locale ? { locale } : undefined,
        body: JSON.stringify(data),
      }),
    }),
    submitAdyenDetails: build.mutation<AdyenAdvancedAttemptResponse, AdyenAdvancedAttemptParams>({
      query: ({ transactionId, data, locale }) => ({
        url: `/payment/${encodeURIComponent(transactionId)}/details`,
        method: 'POST',
        params: locale ? { locale } : undefined,
        body: JSON.stringify(data),
      }),
    }),
    getPaymentStatus: build.query<PaymentStatusResponse, string>({
      query: (transactionId) => ({
        url: `/payment/${transactionId}/status`,
      }),
    }),
    getPreorderPaymentState: build.query<PaymentStatusResponse, PreorderPaymentStateParams>({
      query: ({ transactionId, reservationId }) => ({
        url: `/payment/${encodeURIComponent(transactionId)}/state`,
        params: reservationId ? { reservationId } : undefined,
      }),
    }),
    getPaymentReceipt: build.query<PaymentReceiptResponse, string>({
      query: (transactionId) => ({
        url: `/payment/${transactionId}/receipt`,
      }),
    }),
    emailPaymentReceipt: build.mutation<void, { transactionId: string; locale?: string }>({
      query: ({ transactionId, locale }) => ({
        url: `/payment/${encodeURIComponent(transactionId)}/receipt-email`,
        method: 'POST',
        params: locale ? { locale } : undefined,
      }),
    }),
    createReceiptLink: build.mutation<{ url: string }, { transactionId: string; locale?: string }>({
      query: ({ transactionId, locale }) => ({
        url: `/payment/${encodeURIComponent(transactionId)}/receipt-link`,
        method: 'POST',
        params: locale ? { locale } : undefined,
      }),
    }),
    refundPayment: build.mutation<PaymentStatusResponse, RefundPaymentParams>({
      query: ({ transactionId, reservationId, motivation, locale }) => ({
        url: `/payment/${transactionId}/refund`,
        method: 'PUT',
        body: JSON.stringify({ reservationId, motivation, locale }),
      }),
    }),
    patchReservation: build.mutation<Reservation, PatchReservationParams>({
      query: ({ id, ...body }) => ({
        url: `/reservation/${id}`,
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    }),
  }),
});

// Export hooks for usage in function components, which are
// auto-generated based on the defined endpoints
export const {
  useQueryAvailabilityQuery,
  useLazyQueryAvailabilityQuery,
  useCreateReservationMutation,
  useCreateRequestMutation,
  useCreateWaitlistEntryMutation,
  useSendMessageMutation,
  useCancelReservationMutation,
  useGetReservationQuery,
  useLazyGetReservationQuery,
  useGetReservationsQuery,
  useLazyGetReservationsQuery,
  useUpdateReservationMutation,
  useGetEventsQuery,
  useLazyGetEventsQuery,
  useGetPartnerVenuesQuery,
  useGetWaitlistHighDemandQuery,
  useGetPresaleCatalogQuery,
  useCreatePaymentSessionMutation,
  useSubmitAdyenPaymentMutation,
  useSubmitAdyenDetailsMutation,
  useGetPaymentStatusQuery,
  useLazyGetPaymentStatusQuery,
  useGetPreorderPaymentStateQuery,
  useLazyGetPreorderPaymentStateQuery,
  useGetPaymentReceiptQuery,
  useEmailPaymentReceiptMutation,
  useCreateReceiptLinkMutation,
  useRefundPaymentMutation,
  usePatchReservationMutation,
  util: { getRunningQueriesThunk },
} = bookingApi;

export const {
  queryAvailability,
  createReservation,
  createRequest,
  createWaitlistEntry,
  sendMessage,
  cancelReservation,
  getReservation,
  getReservations,
  updateReservation,
  getEvents,
  getWaitlistHighDemand,
  getPresaleCatalog,
  createPaymentSession,
  getPaymentStatus,
  refundPayment,
  patchReservation,
} = bookingApi.endpoints;
